import { useEffect } from 'react';
import { useQuery } from 'urql';

import { isAddressUuid } from '@bodilenergy/address';
import { BbrLookupByAddressDocument, BbrLookupByDawaIdDocument } from '~src/graphql';
import { AddressInput, ProductType, Residence } from '~src/model';
import { deepNullToUndefined, DEFAULT_RESIDENCE } from '~src/util';

import { LEAD_ORIGIN_NORLYS, useLeadOrigin } from '../useLeadOrigin';
import { toAddressInput } from './useOffer';

export const useBbrLookup = (
  address?: AddressInput & { id?: string | number },
  setResidence?: (residence: Partial<Residence>) => void,
  setSelectedProductTypes?: (productTypes: ProductType[]) => void,
  setHasBbrData?: (value: boolean) => void
) => {
  const { origin: leadOrigin } = useLeadOrigin();

  const addressInput = toAddressInput(address);

  // Addresses chosen from the provider carry their DAR address ID; look BBR up by it instead of re-matching text.
  const addressId = address?.id != null ? String(address.id) : undefined;
  const dawaId = isAddressUuid(addressId) ? addressId : undefined;

  const [resultByAddress] = useQuery({
    query: BbrLookupByAddressDocument,
    variables: { address: addressInput! },
    pause: !addressInput || !!dawaId,
    requestPolicy: 'network-only',
  });

  const [resultByDawaId] = useQuery({
    query: BbrLookupByDawaIdDocument,
    variables: { dawaId: dawaId! },
    pause: !dawaId,
    requestPolicy: 'network-only',
  });

  const result = dawaId ? resultByDawaId : resultByAddress;
  const data = dawaId ? resultByDawaId?.data?.bbrLookupByDawaId : resultByAddress?.data?.bbrLookupByAddress;
  const isFetching = result?.fetching;

  useEffect(
    function updateResidence() {
      if (isFetching) {
        return;
      }

      setHasBbrData?.(!!data);

      setResidence?.(
        deepNullToUndefined({
          ...DEFAULT_RESIDENCE,
          ...data,
        })
      );

      const { fuelType } = data?.primaryHeating ?? {};
      if (fuelType === 'districtHeating' && leadOrigin !== LEAD_ORIGIN_NORLYS) {
        setSelectedProductTypes?.(['solar']);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  return {
    data,
    isFetching: result.fetching,
  };
};
